import styled from 'styled-components/macro'
import { theme } from 'styled-tools'
import { media } from 'ui/rwd'
import { Section } from '../HomepageStyledComponents'

const ProductsSection = styled(Section)`
  padding-top: 3.5rem;
  padding-bottom: 3.5rem;
  ${media.sm`
    padding-top: 4.5rem;
    padding-bottom: 4.5rem;
  `}
  ${media.md`
    padding-top: 5.5rem;
    padding-bottom: 5.5rem;
  `}
`

const ProductsIntro = styled.div`
  text-align: left;
  margin-bottom: 2.75rem;
  ${media.sm`
    margin-bottom: 3.25rem;
  `}
`

const ProductsHeading = styled.h2`
  margin: 0;
  font-size: 1.75rem;
  font-weight: bold;
  line-height: 1.2;
  letter-spacing: -0.02em;
  ${media.sm`
    font-size: 2rem;
  `}
`

const ProductsLead = styled.p`
  margin: 0.85rem 0 0;
  font-size: 1.0625rem;
  line-height: 1.6;
  color: ${theme('midGray')};
  ${media.sm`
    font-size: 1.125rem;
  `}
`

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.25rem;
  text-align: left;
  ${media.md`
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    align-items: stretch;
  `}

  &:has(> *:hover) > *::before {
    background-color: ${theme('gray')};
  }

  &:has(> *:hover) > *:hover::before {
    background-color: ${theme('pink')};
  }

  &:has(> *:hover) > *:hover[data-tone='rambla']::before {
    background-color: ${theme('rambla')};
  }
`

const ProductCard = styled.article.attrs(props => ({
  'data-tone': props.$tone
}))`
  position: relative;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: 100%;
  overflow: hidden;
  padding: 1.75rem 1.5rem 2.25rem;
  background-color: ${theme('white')};
  border: 1px solid ${theme('lightGray')};
  box-shadow: 0 2px 12px -6px rgba(30, 27, 28, 0.18);
  text-align: left;

  &::before {
    content: '';
    display: block;
    height: 8px;
    margin: -1.75rem -1.5rem 1.35rem;
    background-color: ${props =>
      props.$tone === 'rambla' ? theme('rambla')(props) : theme('gray')(props)};
    transition: background-color 0.2s ease;
  }

  ${media.sm`
    padding: 2.25rem 2rem 2.5rem;

    &::before {
      margin: -2.25rem -2rem 1.5rem;
    }
  `}
`

const ProductLogo = styled.div`
  position: absolute;
  z-index: 0;
  right: -4rem;
  bottom: -5.5rem;
  width: 18rem;
  height: 18rem;
  pointer-events: none;
  transform: rotate(14deg);
  opacity: 0.18;
  background-color: ${props =>
    props.$tone === 'rambla' ? theme('rambla')(props) : theme('pink')(props)};
  -webkit-mask-image: url(${props => props.$src});
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: contain;
  mask-image: url(${props => props.$src});
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: contain;
  transition: transform 0.35s ease;

  ${props =>
    props.$tone === 'rambla' &&
    `
    transform: rotate(14deg) scale(1.08);
  `}

  ${ProductGrid}:has(> *:hover) & {
    transform: rotate(14deg);
  }

  ${ProductGrid}:has(> *:hover) > *:hover & {
    transform: rotate(14deg) scale(1.08);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

const ProductName = styled.h3`
  position: relative;
  z-index: 1;
  margin: 0;
  font-size: 1.5rem;
  font-weight: bold;
  line-height: 1.15;
  letter-spacing: -0.02em;
  ${media.sm`
    font-size: 1.625rem;
  `}
`

const ProductTagline = styled.p`
  position: relative;
  z-index: 1;
  margin: 0.35rem 0 0;
  font-size: 1.125rem;
  line-height: 1.4;
  color: ${theme('mineShaft')};
`

const ProductBody = styled.p`
  position: relative;
  z-index: 1;
  margin: 1.25rem 0 0;
  font-size: 1.0625rem;
  line-height: 1.6;
  color: ${theme('midGray')};
  ${media.sm`
    font-size: 1.125rem;
  `}
`

const ProductCta = styled.div`
  position: relative;
  z-index: 1;
  margin-top: auto;
  padding-top: 2rem;
`

const ProductLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-weight: bold;
  font-size: 1rem;
  text-decoration: none;
  color: ${theme('black')};
  transition: color 0.2s ease, gap 0.2s ease;

  &::after {
    content: '→';
    transition: transform 0.2s ease;
  }

  &:hover,
  &:focus {
    color: ${theme('pink')};
    text-decoration: none;

    &::after {
      transform: translateX(3px);
    }
  }

  ${ProductCard}[data-tone='rambla'] &:hover,
  ${ProductCard}[data-tone='rambla'] &:focus {
    color: ${theme('rambla')};
  }
`

export {
  ProductsSection,
  ProductsIntro,
  ProductsHeading,
  ProductsLead,
  ProductGrid,
  ProductCard,
  ProductLogo,
  ProductName,
  ProductTagline,
  ProductBody,
  ProductCta,
  ProductLink
}
